import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Avatar, DarkThemeToggle, Dropdown, DropdownItem } from 'flowbite-react';
import { FiUsers } from 'react-icons/fi';
import { HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import avatar from '../../assets/default-avatar.png';

const TOP_TAB_BASE = 'flex items-center justify-center px-3 py-2 leading-tight border';
const TOP_TAB_ACTIVE = 'bg-gray-800 text-gray-200 dark:bg-gray-600';
const TOP_TAB_INACTIVE =
  'bg-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300';

/**
 * Top header — ported from `wf-adminconsole/components/layout/header/Header.tsx`.
 *
 * Layout: WorkFocus logo + "Admin Console" wordmark on the left, two
 * pill-style top tabs (Dashboard / Clients) in the middle, dark-mode
 * toggle + avatar-with-sign-out dropdown on the right.
 *
 * Renders nothing when the user is anonymous — the top header is for
 * signed-in operators only. The `/login` route runs without it.
 */
export function Header(): JSX.Element | null {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const path = useLocation().pathname;
  const navigate = useNavigate();

  if (!isAuthenticated) return null;

  const account = accounts[0];
  const displayName = account?.name ?? account?.username ?? 'Signed in';

  const handleSignOut = (): void => {
    void instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  };

  const isDashboard = path === '/';
  const isClients = path === '/clients' || path.startsWith('/clients/');

  return (
    <header className="relative flex w-full flex-row items-center border-b border-gray-700 bg-gray-700 px-6 py-3 dark:border-gray-700 dark:bg-gray-800">
      <Link to="/" className="flex items-center space-x-3">
        <img
          src="/workfocus-logo-small.svg"
          alt="WorkFocus Logo"
          height={28}
          width={28}
          className="min-w-[28px]"
        />
        <span className="hidden self-center whitespace-nowrap text-lg font-light text-gray-300 sm:block">
          Admin Console
        </span>
      </Link>

      <nav className="absolute left-1/2 -translate-x-1/2" aria-label="Primary">
        <ul className="flex h-9 items-center -space-x-px text-sm">
          <li>
            <button
              type="button"
              aria-label="Dashboard"
              className={twMerge(
                TOP_TAB_BASE,
                'ms-0 rounded-s-lg border-e-0 border-gray-500 dark:border-gray-600',
                isDashboard ? TOP_TAB_ACTIVE : TOP_TAB_INACTIVE,
              )}
              onClick={() => navigate('/')}
            >
              <HiOutlineClipboardDocumentList className="min-w-3 lg:mr-2" />
              <span>Dashboard</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              aria-label="Clients"
              className={twMerge(
                TOP_TAB_BASE,
                'rounded-e-lg border-gray-500 dark:border-gray-600',
                isClients ? TOP_TAB_ACTIVE : TOP_TAB_INACTIVE,
              )}
              onClick={() => navigate('/clients')}
            >
              <FiUsers className="min-w-3 lg:mr-2" />
              <span>Clients</span>
            </button>
          </li>
        </ul>
      </nav>

      <div className="ml-auto flex items-center gap-1">
        <DarkThemeToggle />
        <Dropdown
          className="z-50 rounded"
          arrowIcon={false}
          inline
          label={
            <span data-testid="user-avatar" className="block">
              <Avatar alt={displayName} img={avatar} rounded size="xs" />
            </span>
          }
        >
          <DropdownItem disabled>
            <span className="text-xs text-gray-500" data-testid="user-display-name">
              {displayName}
            </span>
          </DropdownItem>
          <DropdownItem onClick={handleSignOut}>Sign out</DropdownItem>
        </Dropdown>
      </div>
    </header>
  );
}
